import { useEffect, useMemo, useState } from "react";

import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import { Box } from "@mui/material";
import { useSnackbar } from "notistack";

import { losBannedAppraisersApi } from "../../api/losBannedAppraisersApi";
import AdminField from "../admin/shared/AdminField";

// Editable LoanContact + ban fields (server derives keys / metadata). We never
// send _id / registryKey / normalizedLicenseNumber / createdAt.
const FIELDS = [
  { key: "firstName", label: "First Name" },
  { key: "lastName", label: "Last Name" },
  { key: "company", label: "Company", full: true },
  { key: "appraiserLicenseNumber", label: "License #" },
  { key: "appraiserLicenseState", label: "License State" },
  { key: "phone", label: "Phone" },
  { key: "emailAddress", label: "Email" },
  { key: "bannedEffectiveDate", label: "Banned Effective Date", placeholder: "M/D/YYYY" },
  { key: "source", label: "Source", full: true },
  { key: "bannedReason", label: "Reason (why banned)", full: true, multiline: true },
];

const emptyForm = () =>
  FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: "" }), {});

const BannedAppraiserForm = ({ open, record, onClose, onSaved }) => {
  const { enqueueSnackbar } = useSnackbar();
  const isEdit = !!record?._id;
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (record) {
      setForm(
        FIELDS.reduce(
          (acc, f) => ({ ...acc, [f.key]: record[f.key] ?? "" }),
          {},
        ),
      );
    } else {
      setForm(emptyForm());
    }
  }, [open, record]);

  const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  // Require a license OR first+last name (mirrors the backend 422).
  const valid = useMemo(() => {
    const hasLicense = form.appraiserLicenseNumber?.trim();
    const hasName = form.firstName?.trim() && form.lastName?.trim();
    return !!(hasLicense || hasName);
  }, [form]);

  const handleSave = () => {
    if (!valid || saving) return;
    // Build the body from non-empty editable fields; always tag as an Appraiser
    // ban so it matches the extractor's contact shape.
    const body = { loanRole: "Appraiser", banned: true };
    FIELDS.forEach((f) => {
      const v = typeof form[f.key] === "string" ? form[f.key].trim() : form[f.key];
      if (v !== "" && v != null) body[f.key] = v;
    });
    setSaving(true);
    const req = isEdit
      ? losBannedAppraisersApi.edit(record._id, body)
      : losBannedAppraisersApi.add(body);
    req
      .then((saved) => {
        enqueueSnackbar(isEdit ? "Appraiser updated" : "Appraiser banned", {
          variant: "success",
        });
        onSaved?.(saved);
      })
      .catch((err) =>
        enqueueSnackbar(
          err?.response?.data?.detail || "Couldn't save appraiser",
          { variant: "error" },
        ),
      )
      .finally(() => setSaving(false));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>
        {isEdit ? "Edit Banned Appraiser" : "Add Banned Appraiser"}
      </DialogTitle>
      <DialogContent dividers>
        {/* Label ABOVE the control, the admin form convention (ADMIN_UI.md) —
            the TextField never gets a `label`, which is what kills the notch.
            `alignItems: start` so a wrapped label can't push its row's controls
            out of line with the column beside it. */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            alignItems: "start",
            columnGap: 2,
            rowGap: 2.25,
            pt: 0.5,
          }}
        >
          {FIELDS.map((f) => (
            <AdminField
              key={f.key}
              label={f.label}
              sx={{ gridColumn: f.full ? "1 / -1" : "auto" }}
            >
              <TextField
                placeholder={f.placeholder}
                value={form[f.key] ?? ""}
                onChange={(e) => set(f.key, e.target.value)}
                size="small"
                multiline={f.multiline}
                minRows={f.multiline ? 2 : undefined}
                fullWidth
              />
            </AdminField>
          ))}
        </Box>
        {!valid && (
          <Box sx={{ fontSize: 12, color: "#b91c1c", mt: 1.5 }}>
            Enter a license number, or both first and last name.
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button variant="outlined" color="inherit" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!valid || saving}
          loading={saving}
        >
          {isEdit ? "Save Changes" : "Add to Banned List"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BannedAppraiserForm;
